<template>
  <Stack class="stk-p-6">
    <Buttons justify="between">
      <Button
        :to="{ name: 'ai-jobs'}"
        spacing="tight"
        icon-before="fas fa-arrow-left"
      >
        Go Back
      </Button>

      <Button
        variant="danger"
        spacing="tight"
        @click="handleDelete"
      >
        Delete Job
      </Button>
    </Buttons>

    <LoaderWithText v-if="!hasFetched" />

    <Stack v-else>
      <JobForm
        :form="form"
        :loading="isUpdating"
        @submit="handleUpdating"
      />

      <Stack gap="2">
        <Label>Last 10 runs</Label>

        <Buttons size="3_5">
          <Button
            :icon-before="isFetchingRuns ? `fas fa-sync fa-spin` : `fas fa-sync`"
            :disabled="isFetchingRuns"
            @click="jobsStore.fetchRuns(jobId)"
          >
            Refresh
          </Button>

          <Button
            variant="primary"
            :disabled="isFetchingRuns"
            @click="jobsStore.runJob(jobId)"
          >
            New Run
          </Button>
        </Buttons>

        <div
          v-if="job.runs.length"
          class="runs-list"
        >
          <table class="stk-table">
            <thead class="stk-table-head">
              <tr>
                <th class="stk-table-head-col">
                  Date
                </th>
                <th class="stk-table-head-col">
                  Status
                </th>
                <th class="stk-table-head-col">
                  Result
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="run in job.runs"
                :key="run.id"
                class="stk-table-row"
              >
                <td class="stk-table-cell">
                  {{ formatDate(run.ran_at) }}
                </td>

                <td class="stk-table-cell run-status">
                  <Badge :status="run.status">
                    {{ run.status }}
                  </Badge>
                </td>

                <td class="stk-table-cell run-result">
                  <Button
                    :disabled="!run.result"
                    variant="primary"
                    size="3_5"
                    @click="handleViewResult(run.result)"
                  >
                    View Result
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else>
          No runs found.
        </div>
      </Stack>
    </Stack>

    <StakeModal v-model="showResult">
      <div
        class="content"
        v-html="resultContent"
      />
    </StakeModal>
  </Stack>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { marked } from 'marked';
import { ToastProgrammatic as Toast } from 'buefy';
import { formatDate } from '@frontend/shared/Util.js';
import { useJobsStore } from '@frontend/ui/stores/jobsStore';
import { Badge, Button, Buttons, Label, LoaderWithText, Stack, StakeModal } from '@stakeui';
import JobForm from '../components/JobForm.vue';

const jobsStore = useJobsStore();

const props = defineProps({
  jobId: {
    type: [Number, String],
    required: true,
  },
});

const hasFetched = ref(false);
const showResult = ref(false);
const isUpdating = ref(false);
const resultContent = ref('');

const form = reactive({
  title: '',
  prompt: '',
  cron: '',
  recipients: [],
  enabled: true,
});

const job = computed(() => jobsStore.getJobById(props.jobId));
const isFetchingRuns = computed(() => jobsStore.isFetchingRuns(props.jobId));

const handleViewResult = result => {
  resultContent.value = marked.parse(result.text);
  showResult.value = true;
};

const handleUpdating = async updates => {
  isUpdating.value = true;

  try {
    await jobsStore.updateJob(props.jobId, updates);

    Toast.open({
      message: `Job successfully updated.`,
      type: 'is-success',
    });
  } catch {
    Toast.open({
      message: `There was an error updating the job.`,
      type: 'is-danger',
    });
  } finally {
    isUpdating.value = false;
  }
};

const handleDelete = async () => {
  if (!window.confirm('Are you sure you want to delete this job?')) {
    return;
  }

  try {
    await jobsStore.deleteJob(props.jobId);

    Toast.open({
      message: `Job successfully deleted.`,
      type: 'is-success',
    });

    // Ideally we'd use useRouter here
    window.location.href = '/ui/advisor/ai-jobs';
  } catch (err) {
    console.error('Job deletion failed', err);

    Toast.open({
      message: `There was an error deleting the job.`,
      type: 'is-danger',
    });
  }
};

watch(
    job,
    newJob => {
      if (!newJob) return;
      form.title = newJob.title || '';
      form.prompt = newJob.prompt || '';
      form.cron = newJob.cron || '';
      form.recipients = newJob.recipients || [];
      form.enabled = newJob.enabled || true;
    },
    { immediate: true },
);

onMounted(async () => {
  if (!props.jobId) {
    return hasFetched.value = true;
  }

  await jobsStore.fetchSingleJob(props.jobId);
  hasFetched.value = true;
});
</script>

<style scoped>
.runs-list {
  border: solid var(--stk-border);
  border-width: 0 1px;
}

.run-status {
  text-transform: capitalize;
  width: 10rem;
}

.run-result {
  width: 10rem;
}

.stk-table-row:hover {
  background-color: var(--stk-text-95);
}
</style>


